import axios from "axios";
import { ALL_PERMISSIONS } from "@/permissions";
import { useMainStore } from "@/store";

/**
 * DCTL-2184 - admin impersonation ("view as") client helpers.
 *
 * Impersonation is a server-side session-identity swap; the SPA only learns
 * about it from GET /users/me (payload.user is the target, payload.impersonator
 * is the admin). After a swap - start OR stop - we re-read /users/me plus the
 * new identity's permission map and push both into the store, mirroring the
 * bootstrap rehydrate in main.js. Doing it explicitly (rather than relying on
 * the router guard, which only re-fetches on an actual navigation) re-scopes the
 * whole app from ANY route, including when start/exit happens on the landing
 * route itself.
 */
async function rehydrateSession() {
	const store = useMainStore();

	const { data: me } = await axios.get("/users/me");
	store.setUser({
		...me.payload.user,
		impersonator: me.payload.impersonator ?? null,
	});

	const uuid = store.user.uuid;
	const { data: perms } = await axios.get(`/users/${uuid}/permissions`, {
		params: { permissions: ALL_PERMISSIONS },
	});
	store.setPermissions({
		isAdmin: perms.isAdmin,
		isInternalXlsxReviewer: perms.isInternalXlsxReviewer,
		canManageSpecialProjectFlags: perms.canManageSpecialProjectFlags,
		isProspect: perms.isProspect,
		permissions: perms.permissions,
		userUuid: uuid,
	});
}

/** Start viewing the app as `userUuid`. Admin-only; the server refuses admins. */
export async function startImpersonation(userUuid) {
	await axios.post(`/users/${userUuid}/impersonate`);
	await rehydrateSession();
}

/** Exit impersonation and restore the original admin identity. */
export async function stopImpersonation() {
	await axios.post("/impersonation/stop");
	await rehydrateSession();
}
